import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { submitLead } from "@/lib/api";
import { TRIP_TYPES, GROUP_SIZES } from "@/data/siteData";
import { getReferral } from "@/lib/referral";

const inputCls = (dark) =>
    `w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition-colors focus:border-saffron ${
        dark
            ? "bg-navy-deep/60 border-white/15 text-white placeholder:text-white/40"
            : "bg-white border-stone-300 text-ink placeholder:text-stone-400"
    }`;

export function LeadForm({ idPrefix = "lead", defaultTripType = "", source = "website", dark = false, buttonLabel = "Get My Trip Plan", onSuccess }) {
    const [form, setForm] = useState({
        name: "", phone: "", email: "",
        trip_type: defaultTripType, group_size: "", destination: "", travel_date: "",
        associate_code: getReferral(),
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const setPhone = (e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 12) }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please add your name and phone number.");
            return;
        }
        if (!/^\d{10,12}$/.test(form.phone)) {
            toast.error("Please enter a valid 10-digit mobile number.");
            return;
        }
        setLoading(true);
        try {
            await submitLead({ ...form, source });
            setDone(true);
            toast.success("Awesome! Our Trip Expert will get in touch with you shortly.");
            onSuccess && onSuccess();
        } catch (err) {
            toast.error("Could not submit right now. Please WhatsApp us instead.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className={`rounded-2xl border p-6 text-center ${dark ? "border-white/15 bg-navy-deep/60" : "border-green-200 bg-green-50"}`} data-testid={`${idPrefix}-success`}>
                <div className={`font-display text-lg font-black ${dark ? "text-white" : "text-navy"}`}>Awesome!</div>
                <p className={`mt-1 text-sm ${dark ? "text-white/70" : "text-stone-600"}`}>
                    Our Trip Expert will get in touch with you shortly.
                </p>
            </div>
        );
    }

    const labelCls = `block text-[11px] font-bold uppercase tracking-wider mb-1 ${dark ? "text-white/50" : "text-stone-500"}`;

    return (
        <form onSubmit={handleSubmit} className="space-y-3" data-testid={`${idPrefix}-form`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className={labelCls} htmlFor={`${idPrefix}-name`}>Name *</label>
                    <input id={`${idPrefix}-name`} data-testid={`${idPrefix}-name-input`} className={inputCls(dark)} placeholder="Your name" value={form.name} onChange={set("name")} />
                </div>
                <div>
                    <label className={labelCls} htmlFor={`${idPrefix}-phone`}>Phone / WhatsApp *</label>
                    <input id={`${idPrefix}-phone`} data-testid={`${idPrefix}-phone-input`} type="tel" inputMode="numeric" autoComplete="tel" maxLength={12} className={inputCls(dark)} placeholder="10-digit mobile" value={form.phone} onChange={setPhone} />
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className={labelCls} htmlFor={`${idPrefix}-type`}>Trip Type</label>
                    <select id={`${idPrefix}-type`} data-testid={`${idPrefix}-trip-type-select`} className={inputCls(dark)} value={form.trip_type} onChange={set("trip_type")}>
                        <option value="">Select trip type</option>
                        {TRIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls} htmlFor={`${idPrefix}-size`}>Travellers</label>
                    <select id={`${idPrefix}-size`} data-testid={`${idPrefix}-group-size-select`} className={inputCls(dark)} value={form.group_size} onChange={set("group_size")}>
                        <option value="">Group size</option>
                        {GROUP_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className={labelCls} htmlFor={`${idPrefix}-dest`}>Destination</label>
                    <input id={`${idPrefix}-dest`} data-testid={`${idPrefix}-destination-input`} className={inputCls(dark)} placeholder="Goa, Manali… or Surprise me" value={form.destination} onChange={set("destination")} />
                </div>
                <div>
                    <label className={labelCls} htmlFor={`${idPrefix}-date`}>Travel Date</label>
                    <input id={`${idPrefix}-date`} data-testid={`${idPrefix}-date-input`} type="date" className={inputCls(dark)} value={form.travel_date} onChange={set("travel_date")} />
                </div>
            </div>
            <div>
                <label className={labelCls} htmlFor={`${idPrefix}-email`}>Email</label>
                <input id={`${idPrefix}-email`} data-testid={`${idPrefix}-email-input`} type="email" className={inputCls(dark)} placeholder="you@example.com" value={form.email} onChange={set("email")} />
            </div>
            <div>
                <label className={labelCls} htmlFor={`${idPrefix}-assoc`}>Associate code (optional)</label>
                <input id={`${idPrefix}-assoc`} data-testid={`${idPrefix}-associate-code-input`} className={inputCls(dark)} placeholder="e.g. GCE-XXXXXX" value={form.associate_code} onChange={(e) => setForm((f) => ({ ...f, associate_code: e.target.value.toUpperCase().slice(0, 11) }))} />
            </div>
            <button
                type="submit"
                data-testid={`${idPrefix}-submit-button`}
                disabled={loading}
                className="w-full rounded-full bg-saffron hover:bg-saffron-dark text-white font-display font-black text-sm uppercase tracking-wider py-3.5 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {buttonLabel}
            </button>
        </form>
    );
}
